// modules
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DocumentEditorModule } from '@txtextcontrol/tx-ng-document-editor';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppRoutingModule } from './app-routing.module';
import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DatePipe, Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HighlighterPipe } from './helpers/highlighter.pipe';
import { SanitizeHtmlPipe } from './helpers/sanitizeHtml.pipe';
import { CheckOverflowDirective } from './helpers/check-overflow.directive';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

// components
import { AppComponent } from './app.component';
import { ToastComponent } from './components/toast/toast.component';
import { LoginComponent } from './components/login/login.component';
import { EditDocumentComponent } from './components/edit-document/edit-document.component';
import { DocumentDataDialogComponent } from './components/dialogs/document-data-dialog/document-data-dialog.component';
import { DocumentActionDialogComponent } from './components/dialogs/document-action-dialog/document-action-dialog.component';
import { FormFieldDialogComponent } from './components/dialogs/form-field-dialog/form-field-dialog.component';
import { OpenDocumentComponent } from './components/dialogs/file-menu-dialog/open-document/open-document.component';
import { AnswersChangedDialogComponent } from './components/dialogs/answers-changed-dialog/answers-changed-dialog.component';
import { FileMenuDialogComponent } from './components/dialogs/file-menu-dialog/file-menu-dialog.component';
import { NewDocumentComponent } from './components/dialogs/file-menu-dialog/new-document/new-document.component';
import { SaveAsComponent } from './components/dialogs/file-menu-dialog/save-as/save-as.component';
import { UnsavedChangesDialogComponent } from './components/dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { ClientCaseTreeComponent } from './components/dialogs/file-menu-dialog/client-case-tree/client-case-tree.component';
import { DialogConfirmComponent } from './components/dialogs/dialog-confirm/dialog-confirm.component';
import { DialogInfoComponent } from './components/dialogs/dialog-info/dialog-info.component';
import { UploadDocumentComponent } from './components/dialogs/file-menu-dialog/upload-document/upload-document.component';
import { LoaderWrapperComponent } from './components/loader-wrapper/loader-wrapper.component';
import { LoaderComponent } from './components/loader/loader.component';
import { DownloadDocumentComponent } from './components/dialogs/file-menu-dialog/download-document/download-document.component';
import { ClientCaseDocumentsComponent } from './components/dialogs/file-menu-dialog/open-document/client-case-documents/client-case-documents.component';
import { BrowseDocumentsComponent } from './components/dialogs/file-menu-dialog/open-document/browse-documents/browse-documents.component';
import { HeaderComponent } from './components/header/header.component';
import { RecoverDocumentsDialogComponent } from './components/dialogs/recover-documents-dialog/recover-documents-dialog.component';
import { SendDocumentDialogComponent } from './components/dialogs/send-document-dialog/send-document-dialog.component';
import { SnippetAddDialogComponent } from './components/dialogs/snippet-add-dialog/snippet-add-dialog.component';
import { SnippetListDialogComponent } from './components/dialogs/snippet-list-dialog/snippet-list-dialog.component';
import { FiletypeDropdownComponent } from './components/file-type-selector/file-type-selector.component';
import { RibbonClipboardOverlayComponent } from './components/ribbon-clipboard-overlay/ribbon-clipboard-overlay.component';
import { TxdocsRibbonBarComponent } from './components/txdocs-ribbon-bar/txdocs-ribbon-bar.component';
import { SectionNavigateFieldsComponent } from './components/txdocs-ribbon-bar/section-navigate-fields/section-navigate-fields.component';
import { SectionSearchFieldsComponent } from './components/txdocs-ribbon-bar/section-search-fields/section-search-fields.component';
import { SectionPortalComponent } from './components/txdocs-ribbon-bar/section-portal/section-portal.component';
import { SectionSignaturesComponent } from './components/txdocs-ribbon-bar/section-signatures/section-signatures.component';
import { SectionSignaturesDropdownComponent } from './components/txdocs-ribbon-bar/section-signatures/dropdown/section-signatures-dropdown/section-signatures-dropdown.component';
import { SectionAddressesComponent } from './components/txdocs-ribbon-bar/section-addresses/section-addresses.component';
import { SectionSnippetsComponent } from './components/txdocs-ribbon-bar/section-snippets/section-snippets.component';
import { DialogOfficialsDirectoryComponent } from './components/txdocs-ribbon-bar/section-addresses/dialog-officials-directory/dialog-officials-directory.component';
import { DropdownWithFilterComponent } from './components/txdocs-ribbon-bar/dropdown-with-filter/dropdown-with-filter.component';
import { PdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import { DialogPromptComponent } from './components/dialogs/dialog-prompt/dialog-prompt.component';
import { DebugDialogComponent } from './components/dialogs/debug-dialog/debug-dialog.component';
import { MaintenanceBarComponent } from './components/maintenance-bar/maintenance-bar.component';
import { LoggedOutComponent } from './components/logged-out/logged-out.component';
import { ZoomSliderComponent } from './components/pdf-viewer/zoom-slider/zoom-slider.component';
import { FormFieldInformationDialogComponent } from './components/dialogs/form-field-information-dialog/form-field-information-dialog.component';
import { PDFSectionPortalComponent } from './components/txdocs-pdf-ribbon-bar/pdf-section-portal.component';
import { UnauthenticatedPagesWrapperComponent } from './components/unauthenticated-pages-wrapper/unauthenticated-pages-wrapper.component';
import { TimedOutComponent } from './components/timed-out/timed-out.component';
import { PdfSavedDialogComponent } from './components/dialogs/pdf-saved-dialog/pdf-saved-dialog.component';
import { SessionTakeoverDialogComponent } from './components/dialogs/session-takeover-dialog/session-takeover-dialog.component';
import { SectionFindAndReplaceComponent } from './components/txdocs-ribbon-bar/section-find-and-replace/section-find-and-replace.component';
import { FindAndReplaceDialogComponent } from './components/dialogs/find-and-replace-dialog/find-and-replace-dialog.component';
import { SpotlightComponent } from './components/spotlight.component';
import { WebsocketClosedDialogComponent } from './components/dialogs/websocket-closed-dialog/websocket-closed-dialog.component';
import { QuicktextDialogComponent } from './components/dialogs/quicktext-dialog/quicktext-dialog.component';
import { PendingTakeoverSessionsDialogComponent } from './components/dialogs/pending-takeover-sessions-dialog/pending-takeover-sessions-dialog.component';
import { ContentUpdateComponent } from './components/content-update/content-update.component';
import { SectionLetterheadComponent } from './components/txdocs-ribbon-bar/section-letterhead/section-letterhead.component';
import { RibbonClipboardPasteComponent } from './components/ribbon-clipboard-paste/ribbon-clipboard-paste.component';
import { PasteDialogComponent } from './components/dialogs/paste-dialog/paste-dialog.component';

// directives
import { DragDropFileUploadDirective } from './directives/drag-drop-file-upload.directive';
import { MaskitoDirective } from '@maskito/angular';
import { TooltipIfTruncatedDirective } from './directives/tooltip-if-truncated.directive';

// services
import { DocumentDataService } from './services/document-data.service';
import { LogicService } from './services/logic.service';
import { AppInsightsService } from './services/app-insights.service';
import { TextControlService } from './services/text-control.service';
import { TextControlFormFieldsService } from './services/text-control-formfields.service';
import { AuthenticationService } from './services/authentication.service';
import { ToastService } from './services/toast.service';
import { ApiService } from './services/api.service';
import { CaseService } from './services/case.service';
import { ContactService } from './services/contact.service';
import { DocumentService } from './services/document.service';
import { PortalService } from './services/portal.service';
import { StaffService } from './services/staff.service';
import { OnlineClientService } from './services/online-client.service';
import { UserDefService } from './services/user-def.service';
import { AutoCompleteService } from './services/auto-complete.service';
import { ShortcutService } from './services/shortcut.service';
import { SnippetService } from './services/snippet-service';
import { DialogService } from './services/dialog.service';
import { FolderService } from './services/folder.service';
import { AutosaveService } from './services/autosave.service';
import { GovernmentService } from './services/government.service';
import { OpAttorneyService } from './services/opattorney.service';
import { ErrorHandlerService } from './services/errorHandler.service';
import { PreferencesService } from './services/preferences.service';
import { PdfDocumentService } from './services/pdf-document.service';
import { PdfMetadataService } from './services/pdf-metadata.service';
import { MaintenanceService } from './services/maintenance.service';
import { HelperService } from './services/helper.service';
import { SessionService } from './services/session.service';
import { AnswerLockService } from './services/answer-lock.service';
import { LoadingService } from './services/loading.service';
import { DictionaryService } from './services/dictionary.service';
import { SharedMessageService } from './services/shared-message.service';
import { LetterheadService } from './services/letterhead.service';
import { ClipboardService } from './services/clipboard.service';

// interceptors
import { AuthenticationInterceptor } from './interceptors/authentication.interceptor';

@NgModule({
  declarations: [
    AppComponent,
    EditDocumentComponent,
    DocumentDataDialogComponent,
    DocumentActionDialogComponent,
    FormFieldDialogComponent,
    ToastComponent,
    LoginComponent,
    OpenDocumentComponent,
    NewDocumentComponent,
    SaveAsComponent,
    AnswersChangedDialogComponent,
    FileMenuDialogComponent,
    UnsavedChangesDialogComponent,
    ClientCaseTreeComponent,
    UploadDocumentComponent,
    DragDropFileUploadDirective,
    DialogConfirmComponent,
    DialogInfoComponent,
    LoaderWrapperComponent,
    LoaderComponent,
    DownloadDocumentComponent,
    ClientCaseDocumentsComponent,
    BrowseDocumentsComponent,
    HeaderComponent,
    RecoverDocumentsDialogComponent,
    SendDocumentDialogComponent,
    SnippetAddDialogComponent,
    SnippetListDialogComponent,
    FiletypeDropdownComponent,
    TxdocsRibbonBarComponent,
    SectionNavigateFieldsComponent,
    SectionSearchFieldsComponent,
    SectionPortalComponent,
    SectionSignaturesComponent,
    SectionSignaturesDropdownComponent,
    SectionAddressesComponent,
    SectionSnippetsComponent,
    DialogOfficialsDirectoryComponent,
    DropdownWithFilterComponent,
    DialogPromptComponent,
    DebugDialogComponent,
    PdfViewerComponent,
    MaintenanceBarComponent,
    LoggedOutComponent,
    ZoomSliderComponent,
    FormFieldInformationDialogComponent,
    UnauthenticatedPagesWrapperComponent,
    TimedOutComponent,
    PDFSectionPortalComponent,
    PdfSavedDialogComponent,
    SessionTakeoverDialogComponent,
    SectionFindAndReplaceComponent,
    FindAndReplaceDialogComponent,
    SpotlightComponent,
    WebsocketClosedDialogComponent,
    PendingTakeoverSessionsDialogComponent,
    HighlighterPipe,
    SanitizeHtmlPipe,
    CheckOverflowDirective,
    QuicktextDialogComponent,
    ContentUpdateComponent,
    SectionLetterheadComponent,
    RibbonClipboardPasteComponent,
    PasteDialogComponent,
  ],
  exports: [CheckOverflowDirective],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    DocumentEditorModule,
    NgbModule,
    RibbonClipboardOverlayComponent,
    KeyboardShortcutsModule.forRoot(),
    AngularSvgIconModule.forRoot(),
    NgxExtendedPdfViewerModule,
    NgxSliderModule,
    MaskitoDirective,
    FormsModule,
    EditorModule,
    TooltipIfTruncatedDirective,
  ],
  providers: [
    AuthenticationService,
    ToastService,
    ApiService,
    TextControlService,
    TextControlFormFieldsService,
    DocumentDataService,
    LogicService,
    CaseService,
    ContactService,
    DocumentService,
    PortalService,
    StaffService,
    OnlineClientService,
    UserDefService,
    AutoCompleteService,
    AppInsightsService,
    FolderService,
    MaintenanceService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    ShortcutService,
    SnippetService,
    DialogService,
    AutosaveService,
    GovernmentService,
    OpAttorneyService,
    PreferencesService,
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    DatePipe,
    PdfDocumentService,
    PdfMetadataService,
    HelperService,
    SessionService,
    AnswerLockService,
    LoadingService,
    DictionaryService,
    SharedMessageService,
    LetterheadService,
    ClipboardService,
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {

  // used to test logging when bootstrapping the module fails
  // constructor() {
  //   throw new Error('Deliberate failure in AppModule constructor');
  // }

}
